import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Search, Building2 } from 'lucide-react';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { ModalDialog } from '../UI/modal-dialog';
import { COMPANY_TYPE_LABELS } from '../../models/watchlistModels';
import { useLocale } from '../../contexts/LocaleContext';
import { formControlClasses, themeClasses } from '../../config/themeClasses';
import { cn } from '../../lib/utils';

const AddToWatchlistModal = ({ companies, onClose, onSubmit }) => {
  const { t } = useLocale();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      priority: 'normal',
      alertEnabled: true,
      companyType: '',
    },
  });

  const filteredCompanies = (companies || []).filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.ticker.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setValue('companyId', company.ticker);
    setSearchTerm(company.name);
  };

  const handleFormSubmit = async (data) => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      await onSubmit({
        companyId: selectedCompany.ticker,
        notes: data.notes,
        priority: data.priority,
        portStartPoint: data.portStartPoint,
        portEndPoint: data.portEndPoint,
        companyType: (data.companyType && data.companyType.trim()) || undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalDialog
      title={t('addSupplierToWatchlistTitle')}
      onClose={onClose}
      closeLabel={t('dialogCloseLabel')}
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="add-watchlist-form"
            disabled={loading || !selectedCompany}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="sm" />
                {t('adding')}
              </span>
            ) : (
              t('addToWatchlist')
            )}
          </Button>
        </>
      }
    >
      <form id="add-watchlist-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        <div>
          <label className={formControlClasses.label}>{t('selectSupplierLabel')}</label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary pointer-events-none"
              aria-hidden
            />
            <Input
              type="text"
              placeholder={t('searchSuppliersPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {searchTerm ? (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border-light">
              {filteredCompanies.length === 0 ? (
                <p className="p-3 text-center text-sm text-text-secondary">{t('noSuppliersFound')}</p>
              ) : (
                filteredCompanies.map((company) => (
                  <button
                    key={company.ticker}
                    type="button"
                    onClick={() => handleCompanySelect(company)}
                    className={cn(
                      'flex w-full items-center gap-2 border-b border-border-light p-3 text-left last:border-b-0 hover:bg-background-primary',
                      selectedCompany?.ticker === company.ticker && 'bg-primary-50'
                    )}
                  >
                    <Building2 className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
                    <div>
                      <p className="text-sm font-medium text-text-primary">{company.name}</p>
                      <p className="text-xs text-text-secondary">
                        {company.ticker} • {company.industry}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : null}

          {selectedCompany ? (
            <div className={cn(themeClasses.appKit.emptyDashed, 'mt-2 py-3 text-left')}>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary-500" aria-hidden />
                <div>
                  <p className="text-sm font-medium text-text-primary">{selectedCompany.name}</p>
                  <p className="text-xs text-text-secondary">
                    {selectedCompany.ticker} • {selectedCompany.industry}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div>
          <label htmlFor="notes" className={formControlClasses.label}>
            {t('notesLabel')}
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
            className={formControlClasses.textarea}
            placeholder={t('notesPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="companyType" className={formControlClasses.label}>
            {t('companyType')}
          </label>
          <select id="companyType" {...register('companyType')} className={formControlClasses.select}>
            <option value="">{t('kanbanUnassigned')}</option>
            {Object.entries(COMPANY_TYPE_LABELS)
              .filter(([k]) => k !== 'unassigned')
              .map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label htmlFor="priority" className={formControlClasses.label}>
            {t('priority')}
          </label>
          <select
            id="priority"
            {...register('priority', { required: t('priorityRequired') })}
            className={formControlClasses.select}
          >
            <option value="low">{t('priorityLow')}</option>
            <option value="normal">{t('priorityNormal')}</option>
            <option value="high">{t('priorityHigh')}</option>
          </select>
          {errors.priority && <p className={formControlClasses.error}>{errors.priority.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="portStartPoint" className={formControlClasses.label}>
              {t('watchlistStartPoint')}
            </label>
            <Input
              id="portStartPoint"
              {...register('portStartPoint')}
              placeholder={t('portStartPlaceholder')}
            />
          </div>
          <div>
            <label htmlFor="portEndPoint" className={formControlClasses.label}>
              {t('watchlistEndPoint')}
            </label>
            <Input id="portEndPoint" {...register('portEndPoint')} placeholder={t('portEndPlaceholder')} />
          </div>
        </div>
      </form>
    </ModalDialog>
  );
};

export default AddToWatchlistModal;
